import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Collapse,
  Container,
  Typography,
  Card,
  CardContent,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Skeleton,
  InputAdornment,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import PageLayout from '../components/layout/PageLayout';
import DashboardFilters from '../components/dashboard/DashboardFilters';
import {
  useLazyGetCallsCounterSummaryQuery,
  useLazyGetTeamsQuery,
} from '../store/api';
import { extractList } from '../utils/response';
import { useLazySelectOptions } from '../hooks/useLazySelectOptions';

const toNum = (v) => (v != null && v !== '' ? Number(v) : 0);
const toPct = (v) => (v != null && v !== '' ? `${Number(v).toFixed(1)}%` : '0.0%');

/** Parse "HH:MM:SS" or "H:MM:SS" to total seconds */
function durationToSeconds(val) {
  if (val == null || val === '') return 0;
  const s = String(val).trim();
  const match = s.match(/^(\d+):(\d{2}):(\d{2})$/);
  if (match) return parseInt(match[1], 10) * 3600 + parseInt(match[2], 10) * 60 + parseInt(match[3], 10);
  const match2 = s.match(/^(\d+):(\d{2})$/);
  if (match2) return parseInt(match2[1], 10) * 60 + parseInt(match2[2], 10);
  const n = Number(s);
  return !Number.isNaN(n) && n >= 0 ? n : 0;
}

function formatDuration(val) {
  if (val == null || val === '') return '—';
  const s = String(val).trim();
  if (/^\d+:\d{2}:\d{2}$/.test(s) || /^\d+:\d{2}$/.test(s)) return s;
  const n = Number(s);
  if (!Number.isNaN(n) && n >= 0) {
    const h = Math.floor(n / 3600);
    const m = Math.floor((n % 3600) / 60);
    const sec = Math.floor(n % 60);
    return [h, m, sec].map((x) => String(x).padStart(2, '0')).join(':');
  }
  return s;
}

/** Format total seconds to HH:MM:SS */
function formatDurationFromSeconds(seconds) {
  if (seconds == null || seconds < 0) return '—';
  const s = Math.floor(Number(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return [h, m, sec].map((x) => String(x).padStart(2, '0')).join(':');
}

// Header colors to match screenshot: light green, light blue (Month), red-orange, cyan (Closers), yellow, light orange/gold
const HEADER = {
  lightGreen: '#c8e6c9',
  lightBlue: '#bbdefb',
  redOrange: '#ff8a65',
  cyan: '#b3e5fc',
  yellow: '#fff9c4',
  lightOrange: '#ffcc80',
};

export default function CallsCounterSummary() {
  const [fetchSummary] = useLazyGetCallsCounterSummaryQuery();
  const [fetchTeams] = useLazyGetTeamsQuery();
  const teamsSelect = useLazySelectOptions(fetchTeams, {
    mapResult: (res) => extractList(res),
  });
  const teams = teamsSelect.options;
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(true);

  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [teamId, setTeamId] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const loadTeamsForFilters = useCallback(() => {
    return teamsSelect.load().catch(() => []);
  }, [teamsSelect]);

  useEffect(() => {
    if (filtersOpen) {
      void loadTeamsForFilters();
    }
  }, [filtersOpen, loadTeamsForFilters]);
  const list = useMemo(() => {
    const raw = response?.data;
    return Array.isArray(raw) ? raw : [];
  }, [response]);

  const searchLower = (search || '').toLowerCase().trim();
  const filtered = useMemo(() => {
    if (!searchLower) return list;
    return list.filter((row) => {
      const name = String(row.name ?? '').toLowerCase();
      const userId = String(row.user_id ?? '').toLowerCase();
      const reportMonth = String(row.report_month ?? '').toLowerCase();
      return name.includes(searchLower) || userId.includes(searchLower) || reportMonth.includes(searchLower);
    });
  }, [list, searchLower]);

  const from = page * rowsPerPage;
  const paginated = useMemo(
    () => filtered.slice(from, from + rowsPerPage),
    [filtered, from, rowsPerPage]
  );

  const monthLabel = useMemo(() => {
    if (list.length > 0 && list[0]?.report_month) return list[0].report_month;
    if (dateFrom || dateTo) {
      const d = dateTo ? new Date(dateTo) : new Date(dateFrom);
      if (!Number.isNaN(d.getTime())) {
        const month = d.toLocaleString('default', { month: 'short' });
        const year = String(d.getFullYear()).slice(-2);
        return `${month}-${year}`;
      }
    }
    const now = new Date();
    return now.toLocaleString('default', { month: 'short' }) + '-' + String(now.getFullYear()).slice(-2);
  }, [list, dateFrom, dateTo]);

  const fetchData = useCallback(async () => {
    const params = {};
    if (dateFrom && String(dateFrom).trim()) params.date_from = String(dateFrom).trim();
    if (dateTo && String(dateTo).trim()) params.date_to = String(dateTo).trim();
    if (teamId != null && teamId !== '') params.team_id = String(teamId);
    try {
      setLoading(true);
      const res = await fetchSummary(params).unwrap();
      setResponse(res);
    } catch {
      setResponse(null);
    } finally {
      setLoading(false);
    }
  }, [fetchSummary, dateFrom, dateTo, teamId]);

  const clearFilters = useCallback(async () => {
    setDateFrom('');
    setDateTo('');
    setTeamId('');
    try {
      setLoading(true);
      const res = await fetchSummary({}).unwrap();
      setResponse(res);
    } catch {
      setResponse(null);
    } finally {
      setLoading(false);
    }
  }, [fetchSummary]);

  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional dependency list
  }, []);

  const totals = useMemo(() => {
    if (!filtered.length) return null;
    const callDurationSeconds = filtered.reduce((a, r) => a + durationToSeconds(r.call_duration), 0);
    return {
      no_of_dials: filtered.reduce((a, r) => a + toNum(r.no_of_dials), 0),
      answered_calls: filtered.reduce((a, r) => a + toNum(r.answered_calls), 0),
      call_duration_seconds: callDurationSeconds,
      call_duration_formatted: formatDurationFromSeconds(callDurationSeconds),
      interested_leads: filtered.reduce((a, r) => a + toNum(r.interested_leads), 0),
      leads_gen: filtered.reduce((a, r) => a + toNum(r.leads_gen), 0),
      assigned_leads: filtered.reduce((a, r) => a + toNum(r.assigned_leads), 0),
      approved_leads: filtered.reduce((a, r) => a + toNum(r.approved_leads), 0),
      sales_generated: filtered.reduce((a, r) => a + toNum(r.sales_generated), 0),
    };
  }, [filtered]);

  const handlePageChange = (_, newPage) => setPage(newPage);
  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setPage(0);
  };

  return (
    <PageLayout>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Box
          sx={{
            mb: 3,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Typography variant="h5" fontWeight={600} sx={{ color: 'grey.800' }}>
            Calls Counter Summary
          </Typography>
          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            onClick={() => setFiltersOpen((o) => !o)}
            sx={{
              flexShrink: 0,
              borderColor: '#154A8A',
              color: '#154A8A',
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': { borderColor: '#0d3a6a', bgcolor: 'rgba(21, 74, 138, 0.06)' },
            }}
          >
            {filtersOpen ? 'Hide filters' : 'Filter'}
          </Button>
        </Box>

        <Collapse in={filtersOpen} timeout="auto" unmountOnExit>
          <DashboardFilters
            dateFrom={dateFrom}
            dateTo={dateTo}
            teamFilterId={teamId}
            teams={teams}
            onDateFromChange={setDateFrom}
            onDateToChange={setDateTo}
            onTeamChange={setTeamId}
            onApply={fetchData}
            onClearFilters={clearFilters}
            showTeam
            onTeamOpen={loadTeamsForFilters}
            teamsLoading={teamsSelect.loading}
          />
        </Collapse>

        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2 }}>
              <Typography variant="subtitle1" fontWeight={600}>
                Calls Counter Summary
              </Typography>
              <TextField
                size="small"
                placeholder="Search by name, user ID, or month..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                sx={{ minWidth: 280 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, bgcolor: HEADER.lightGreen }}>User Name</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, bgcolor: HEADER.lightGreen }}>No. of Dials</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, bgcolor: HEADER.lightGreen }}>Answered Calls</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, bgcolor: HEADER.lightGreen }}>Call Duration</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, bgcolor: HEADER.lightBlue, color: 'grey.900', textAlign: 'center' }}>Month</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, bgcolor: HEADER.redOrange, color: 'grey.900', textAlign: 'center' }}>{monthLabel || '—'}</TableCell>
                    <TableCell align="right" colSpan={2} sx={{ fontWeight: 600, bgcolor: HEADER.cyan, color: 'grey.900', textAlign: 'center' }}>Closers</TableCell>
                    <TableCell align="right" colSpan={2} sx={{ fontWeight: 600, bgcolor: HEADER.yellow, color: 'grey.900', textAlign: 'center' }}>Conversion%</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, bgcolor: HEADER.lightGreen }} />
                    <TableCell sx={{ fontWeight: 600, bgcolor: HEADER.lightGreen }} />
                    <TableCell sx={{ fontWeight: 600, bgcolor: HEADER.lightGreen }} />
                    <TableCell sx={{ fontWeight: 600, bgcolor: HEADER.lightGreen }} />
                    <TableCell align="right" sx={{ fontWeight: 600, bgcolor: HEADER.lightGreen }}>Interested Leads (&gt; 1 Mint)</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, bgcolor: HEADER.redOrange }}>Lead Gen</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, bgcolor: HEADER.lightOrange }}>Assigned Leads</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, bgcolor: HEADER.lightGreen }}>Sales Approved</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, bgcolor: HEADER.lightOrange }}>Qualified Lead</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, bgcolor: HEADER.lightGreen }}>Closing</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    [...Array(5)].map((_, i) => (
                      <TableRow key={i}>
                        <TableCell colSpan={10}><Skeleton height={36} /></TableCell>
                      </TableRow>
                    ))
                  ) : filtered.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} sx={{ textAlign: 'center', py: 4 }}>
                        {list.length === 0
                          ? 'No data. Apply filters or clear filters to load all.'
                          : 'No rows match your search.'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    <>
                      {paginated.map((row, i) => (
                        <TableRow key={row.user_id ?? i}>
                          <TableCell sx={{ fontWeight: 600 }}>{row.name ?? '—'} ({row.user_id ?? '—'})</TableCell>
                          <TableCell align="right">{toNum(row.no_of_dials)}</TableCell>
                          <TableCell align="right">{toNum(row.answered_calls)}</TableCell>
                          <TableCell align="right">{formatDuration(row.call_duration)}</TableCell>
                          <TableCell align="right">{toNum(row.interested_leads)}</TableCell>
                          <TableCell align="right">{toNum(row.leads_gen)}</TableCell>
                          <TableCell align="right">{toNum(row.assigned_leads)}</TableCell>
                          <TableCell align="right">{toNum(row.approved_leads)}</TableCell>
                          <TableCell align="right">{toPct(row.qualified_lead)}</TableCell>
                          <TableCell align="right">{toPct(row.closing_lead)}</TableCell>
                        </TableRow>
                      ))}
                      {totals && (
                        <TableRow sx={{ bgcolor: 'action.hover' }}>
                          <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>{totals.no_of_dials}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>{totals.answered_calls}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>{totals.call_duration_formatted}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>{totals.interested_leads}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>{totals.leads_gen}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>{totals.assigned_leads}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>{totals.approved_leads}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>—</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>—</TableCell>
                        </TableRow>
                      )}
                    </>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {!loading && filtered.length > 0 && (
              <TablePagination
                component="div"
                count={filtered.length}
                page={page}
                onPageChange={handlePageChange}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={[5, 10, 25, 50]}
                labelRowsPerPage="Rows per page"
              />
            )}
          </CardContent>
        </Card>
      </Container>
    </PageLayout>
  );
}
